import type { FetchConfig } from '../helper-types';
import type { paths, components } from './portal-api-definition';
import type { CPCVertical } from './vertical-types';

/**
 * Type definition for forwarding users to portal domain.
 */
type PortalUrlOptions = {
	/**
	 * URL that the user ends up at after the action.
	 * @default current-url
	 */
	returnUrl?: string;

	/**
	 * URL that the user ends up when terminating the flow without completing it.
	 * @default current-url
	 */
	cancelUrl?: string;

	/**
	 * Indicates if the completion flow should be skipped.
	 * This is activated for users that have not filled in all the player information.
	 * @default true
	 */
	skipCompletionFlow?: boolean;
};

export function createNloPortalApi(fetchConfig: FetchConfig, portalBaseUrl: string, verticalName: CPCVertical) {
	const client = fetchConfig.createClient<paths>();

	return {
		getPlayer: () => {
			return client.GET('/api/my/common/account/profile');
		},

		resolveAddress: (params: { houseNumber: number; houseNumberExtension?: string; zipCode: string }, abortSignal?: AbortSignal) => {
			return client.GET('/api/common/address/lookup-address/{ZipCode}/{HouseNumber}/{HouseNumberExtension}', {
				params: {
					path: {
						HouseNumber: params.houseNumber,
						HouseNumberExtension: params.houseNumberExtension || '',
						ZipCode: params.zipCode,
					},
				},
				signal: abortSignal,
			});
		},

		getWallet: () => {
			return client.GET('/api/my/common/wallet');
		},

		depositIdeal(request: components['schemas']['DepositIdealRequestViewModel']) {
			return client.POST('/api/my/common/deposit/ideal', { body: request });
		},

		depositCreditCard(request: components['schemas']['DepositCreditCardRequestViewModel']) {
			return client.POST('/api/my/common/deposit/creditcard', { body: request });
		},

		getAgeValidationInfo() {
			return client.GET('/api/my/common/responsible-gaming/age-validation');
		},

		startAgeValidation(data: components['schemas']['IdinAgeValidationRequestViewModel']) {
			return client.POST('/api/my/common/responsible-gaming/age-validation', { body: data });
		},

		/**
		 * Return a URL for the Portal application.
		 * @param path The path to a .NET controller
		 * @param options The options that will be supplied to the portal url.
		 */
		getPortalUrl: (path: string, options?: PortalUrlOptions) => {
			const urlOptions: Required<PortalUrlOptions> = {
				returnUrl: options?.returnUrl || window.location.href,
				cancelUrl: options?.cancelUrl || window.location.href,
				skipCompletionFlow: options?.skipCompletionFlow || true,
			};

			const targetUrl = new URL(path, portalBaseUrl);
			targetUrl.searchParams.append('returnUrl', urlOptions.returnUrl);
			targetUrl.searchParams.append('cancelUrl', urlOptions.cancelUrl);
			targetUrl.searchParams.append('regVertical', verticalName);
			targetUrl.searchParams.append('skipCompletionFlow', urlOptions.skipCompletionFlow.toString());

			return targetUrl.toString();
		},
	};
}
